<template>
  <div class="page">
    <main class="content">
      <section class="guide-detail" v-if="guide">
        <div class="guide-top-actions">
          <button class="back-button" @click="goBackToList">
            <span aria-hidden="true">←</span>
            Back to Guides
          </button>
          <button class="support-button" @click="openSupportServer">
            <fa :icon="['fab', 'discord']" />
            Support Server
          </button>
        </div>

        <header class="guide-header">
          <p class="guide-meta">{{ guide.difficulty }} guide</p>
          <h1 class="guide-title">{{ guide.title }}</h1>
          <div class="guide-summary markdown-body" v-html="renderMarkdown(guide.summary)"></div>
        </header>

        <div class="guide-sections">
          <article
            v-for="(section, index) in guide.sections"
            :key="`${guide.slug}-section-${index}`"
            class="guide-section"
          >
            <h2 class="section-title">
              <span>{{ index + 1 }}.</span>
              <span v-html="renderInlineMarkdown(section.heading)"></span>
            </h2>
            <ol class="section-steps">
              <li v-for="(step, stepIndex) in section.steps" :key="`${guide.slug}-step-${index}-${stepIndex}`">
                <span v-html="renderInlineMarkdown(step)"></span>
              </li>
            </ol>

            <div
              v-if="section.content"
              class="section-markdown markdown-body"
              v-html="renderMarkdown(section.content)"
            ></div>

            <figure v-if="section.image" class="section-image-wrap">
              <img :src="section.image" :alt="section.imageAlt || section.heading" class="section-image">
            </figure>

            <div v-if="section.youtubeId" class="video-wrap">
              <iframe
                class="youtube-embed"
                :src="getYoutubeEmbedUrl(section.youtubeId)"
                :title="`${section.heading} video`"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              ></iframe>
            </div>
          </article>
        </div>
      </section>

      <section class="guide-not-found" v-else>
        <h1>Guide Not Found</h1>
        <p>We could not find that guide. Return to the guide list or ask for help in Discord.</p>
        <div class="not-found-actions">
          <router-link class="read-guide-link" to="/guides">Back to Guides</router-link>
          <button class="support-button" @click="openSupportServer">
            <fa :icon="['fab', 'discord']" />
            Support Server
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import guides from '../data/guides.json';
import { renderInlineMarkdown, renderMarkdown } from '../utils/markdown';

export default {
  name: 'GuideDetailPage',
  data() {
    return {
      guides
    };
  },
  computed: {
    guide() {
      return this.guides.find((item) => item.slug === this.$route.params.slug) || null;
    }
  },
  methods: {
    goBackToList() {
      this.$router.push('/guides');
    },
    openSupportServer() {
      window.open('https://discord.com/invite/beaStFd', '_blank', 'noopener,noreferrer');
    },
    getYoutubeEmbedUrl(youtubeId) {
      return `https://www.youtube-nocookie.com/embed/${youtubeId}`;
    },
    renderInlineMarkdown,
    renderMarkdown
  }
};
</script>

<style scoped>
.guide-detail {
  width: 100%;
  max-width: 900px;
  text-align: left;
}

.guide-top-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.guide-header {
  margin-bottom: var(--spacing-lg);
}

.guide-meta {
  color: #22c1c3;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: var(--font-size-xs);
  margin-bottom: 0.5rem;
}

.guide-title {
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 0.5rem;
  background: linear-gradient(120deg, #fdbb2d, #22c1c3);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.guide-summary {
  color: var(--color-text-secondary);
  margin-bottom: 0;
}

.guide-sections {
  display: grid;
  gap: var(--spacing-md);
}

.guide-section {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
}

.section-title {
  font-size: 1.35rem;
  margin-bottom: var(--spacing-sm);
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.section-steps {
  padding-left: 1.2rem;
  color: var(--color-text-secondary);
  margin: 0;
  display: grid;
  gap: 0.5rem;
}

.section-image-wrap {
  margin-top: var(--spacing-md);
}

.section-markdown {
  margin-top: var(--spacing-md);
}

.section-image {
  width: 100%;
  border-radius: var(--border-radius-md);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: block;
}

.video-wrap {
  margin-top: var(--spacing-md);
}

.youtube-embed {
  width: 100%;
  aspect-ratio: 16 / 9;
  border: none;
  border-radius: var(--border-radius-md);
}

.back-button,
.support-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  border-radius: var(--border-radius-md);
  padding: 0.65rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.back-button {
  background: rgba(255, 255, 255, 0.09);
  color: var(--color-text-primary);
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.16);
}

.support-button {
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
}

.support-button:hover {
  background: var(--color-accent-discord-hover);
}

.guide-not-found {
  width: 100%;
  max-width: 720px;
  text-align: center;
}

.not-found-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: center;
  flex-wrap: wrap;
  margin-top: var(--spacing-md);
}

.read-guide-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.09);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-md);
  padding: 0.65rem 1rem;
}

.markdown-body {
  color: var(--color-text-secondary);
  display: grid;
  gap: 0.75rem;
}

.markdown-body :deep(p) {
  margin: 0;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 0;
  padding-left: 1.2rem;
}

.markdown-body :deep(code) {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.35rem;
  padding: 0.12rem 0.35rem;
  font-family: var(--font-mono);
  font-size: 0.92em;
}

.markdown-body :deep(a) {
  color: #7fe7e8;
  text-decoration: underline;
}

.markdown-body :deep(blockquote) {
  margin: 0;
  border-left: 3px solid rgba(34, 193, 195, 0.6);
  padding-left: 0.8rem;
  color: var(--color-text-secondary);
}

.markdown-body :deep(.markdown-callout) {
  margin: 0;
  border-radius: var(--border-radius-md);
  border: 1px solid rgba(34, 193, 195, 0.35);
  background: linear-gradient(180deg, rgba(34, 193, 195, 0.13), rgba(34, 193, 195, 0.06));
  padding: 0.85rem 1rem;
}

.markdown-body :deep(.markdown-callout-title) {
  margin: 0 0 0.35rem 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: #9ff6f7;
}

.markdown-body :deep(.markdown-callout-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
}

.markdown-body :deep(.markdown-callout-icon-svg) {
  width: 1.2rem;
  height: 1.2rem;
}

.markdown-body :deep(.markdown-callout p) {
  margin: 0;
}

.markdown-body :deep(.markdown-callout-important) {
  border-color: rgba(253, 187, 45, 0.5);
  background: linear-gradient(180deg, rgba(253, 187, 45, 0.15), rgba(253, 187, 45, 0.06));
}

.markdown-body :deep(.markdown-callout-important .markdown-callout-title) {
  color: #ffe29b;
}

.markdown-body :deep(.markdown-callout-warning) {
  border-color: rgba(255, 127, 127, 0.55);
  background: linear-gradient(180deg, rgba(255, 127, 127, 0.17), rgba(255, 127, 127, 0.07));
}

.markdown-body :deep(.markdown-callout-warning .markdown-callout-title) {
  color: #ffb2b2;
}

.markdown-body :deep(.markdown-callout-tip) {
  border-color: rgba(82, 210, 115, 0.45);
  background: linear-gradient(180deg, rgba(82, 210, 115, 0.16), rgba(82, 210, 115, 0.06));
}

.markdown-body :deep(.markdown-callout-tip .markdown-callout-title) {
  color: #9df0b3;
}

@media (max-width: 768px) {
  .guide-top-actions {
    align-items: stretch;
  }

  .back-button,
  .support-button {
    width: 100%;
  }
}
</style>
